import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api, { getImageUrl } from '../../utils/api';
import { toast } from 'react-toastify';
import { Link, useSearchParams } from 'react-router-dom';

interface MediaFolderSummary {
    _id: string;
    folderNumber: number;
    folderName: string;
    description?: string;
    coverImage?: string;
    previewThumbnail?: string;
    floorCount: number;
    galleryCount: number;
    docCount: number;
    totalFiles: number;
    createdAt: string;
    updatedAt: string;
}

interface MediaFolderDetail {
    _id: string;
    folderNumber: number;
    folderName: string;
    description?: string;
    coverImage?: string;
    floorVariants: {
        _id?: string;
        title: string;
        unitType?: string;
        priceRange?: string;
        subUnitVariant: string;
        variantType: string;
        url: string;
        filename?: string;
    }[];
    gallery: {
        exterior: string[];
        interior: string[];
        amenities: string[];
        construction: string[];
        topPhotos: string[];
        other: string[];
    };
    documents: {
        brochure?: string;
        tpImage?: string;
        legalDocuments: {
            _id?: string;
            title: string;
            url: string;
            filename?: string;
        }[];
    };
    createdAt: string;
    updatedAt: string;
}

export default function AdminMedia() {
    const { token } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeFolderParam = searchParams.get('folder');

    const [folders, setFolders] = useState<MediaFolderSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Active folder detail
    const [activeFolder, setActiveFolder] = useState<MediaFolderDetail | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [activeTab, setActiveTab] = useState<'floorVariants' | 'gallery' | 'documents' | 'cover'>('floorVariants');
    const [galleryAlbum, setGalleryAlbum] = useState<'exterior' | 'interior' | 'amenities' | 'construction' | 'topPhotos' | 'other'>('exterior');

    // Create Modal
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newFolderNumber, setNewFolderNumber] = useState<number | ''>('');
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderDesc, setNewFolderDesc] = useState('');
    const [creating, setCreating] = useState(false);

    // Upload state
    const [uploading, setUploading] = useState(false);
    const [variantLabel, setVariantLabel] = useState('');
    const [variantType, setVariantType] = useState('2D');
    const [docTitle, setDocTitle] = useState('');

    // Unit Type Tabs State (e.g. 2 BHK, 3 BHK, 4 BHK)
    const [selectedUnitTypeTab, setSelectedUnitTypeTab] = useState<string>('All');
    const [uploadUnitType, setUploadUnitType] = useState<string>('2 BHK');
    const [uploadPriceRange, setUploadPriceRange] = useState<string>('On Request');
    const [customUnitTabs, setCustomUnitTabs] = useState<string[]>([]);
    const [isAddUnitTypeModalOpen, setIsAddUnitTypeModalOpen] = useState(false);
    const [newUnitTypeName, setNewUnitTypeName] = useState('');
    const [newUnitTypePrice, setNewUnitTypePrice] = useState('On Request');

    // Lightbox / Image preview modal
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    // ZIP Upload Modal State
    const [isZipModalOpen, setIsZipModalOpen] = useState(false);
    const [zipTargetMode, setZipTargetMode] = useState<'new' | 'bulk' | 'existing'>('new');
    const [zipSelectedFolder, setZipSelectedFolder] = useState<number | ''>('');
    const [zipNewFolderNumber, setZipNewFolderNumber] = useState<number | ''>('');
    const [zipNewFolderName, setZipNewFolderName] = useState('');
    const [zipNewFolderDesc, setZipNewFolderDesc] = useState('');
    const [zipFile, setZipFile] = useState<File | null>(null);
    const [zipFiles, setZipFiles] = useState<File[]>([]);
    const [zipUploading, setZipUploading] = useState(false);

    // Backup downloading state (holds folderNumber or 'all' while downloading)
    const [downloadingBackup, setDownloadingBackup] = useState<number | 'all' | null>(null);

    // Edit Folder Modal State
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editFolderNumber, setEditFolderNumber] = useState<number>(1);
    const [newAssignedNumber, setNewAssignedNumber] = useState<number | ''>(1);
    const [editFolderName, setEditFolderName] = useState('');
    const [editFolderDesc, setEditFolderDesc] = useState('');
    const [savingFolder, setSavingFolder] = useState(false);

    // Edit Floor Variant Modal State
    const [editingVariant, setEditingVariant] = useState<{
        itemId?: string;
        url: string;
        title: string;
        unitType: string;
        priceRange: string;
        subUnitVariant: string;
        variantType: string;
    } | null>(null);
    const [savingVariant, setSavingVariant] = useState(false);

    // Edit Legal Document Title Modal State
    const [editingDoc, setEditingDoc] = useState<{
        itemId?: string;
        url: string;
        title: string;
    } | null>(null);
    const [savingDoc, setSavingDoc] = useState(false);

    // Selection States for Bulk Delete
    const [selectedFolders, setSelectedFolders] = useState<number[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<Array<{ subFolder: string; filePath: string; fileId?: string }>>([]);

    // Custom Delete Confirmation Dialog State
    const [deleteDialog, setDeleteDialog] = useState<{
        type: 'folder' | 'file' | 'bulk-folders' | 'bulk-files';
        folderNumber?: number;
        folderNumbers?: number[];
        fileParams?: { subFolder: string; filePath: string; fileId?: string };
        filesList?: Array<{ subFolder: string; filePath: string; fileId?: string }>;
        title: string;
        message: string;
        subMessage?: string;
    } | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleOpenEditFolderModal = (folderToEdit: { folderNumber: number, folderName: string, description?: string }, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setEditFolderNumber(folderToEdit.folderNumber);
        setNewAssignedNumber(folderToEdit.folderNumber);
        setEditFolderName(folderToEdit.folderName);
        setEditFolderDesc(folderToEdit.description || '');
        setIsEditModalOpen(true);
    };

    const handleSaveEditFolder = async (e: React.FormEvent) => {
        e.preventDefault();
        setSavingFolder(true);
        try {
            const res = await api.updateMediaFolder(token || undefined, editFolderNumber, {
                folderName: editFolderName,
                description: editFolderDesc,
                newFolderNumber: newAssignedNumber !== editFolderNumber ? newAssignedNumber : undefined
            });

            if (res && res.folder) {
                toast.success('Folder updated successfully!');
                setIsEditModalOpen(false);
                await fetchFolders();
                if (activeFolder && (activeFolder.folderNumber === editFolderNumber || activeFolder.folderNumber === newAssignedNumber)) {
                    if (newAssignedNumber && newAssignedNumber !== editFolderNumber) {
                        setSearchParams({ folder: String(newAssignedNumber) });
                    } else {
                        fetchFolderDetail(editFolderNumber);
                    }
                }
            } else {
                toast.error(res?.message || res?.error || 'Failed to update folder');
            }
        } catch (err: any) {
            console.error('Update folder error:', err);
            toast.error(err.message || 'Error updating folder');
        } finally {
            setSavingFolder(false);
        }
    };

    const handleSetCoverImage = async (filePath: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        if (!activeFolder) return;
        try {
            const res = await api.updateMediaFolderItem(token || undefined, activeFolder.folderNumber, {
                itemType: 'set-cover',
                filePath
            });
            if (res && res.folder) {
                toast.success('Set as Cover Image!');
                setActiveFolder(res.folder);
                fetchFolders();
            } else {
                toast.error(res?.message || 'Failed to set cover image');
            }
        } catch (err) {
            toast.error('Failed to set cover image');
        }
    };

    const handleMoveGalleryAlbum = async (filePath: string, fromAlbum: string, toAlbum: string) => {
        if (!activeFolder || fromAlbum === toAlbum) return;
        try {
            const res = await api.updateMediaFolderItem(token || undefined, activeFolder.folderNumber, {
                itemType: 'move-gallery',
                filePath,
                fromAlbum,
                toAlbum
            });
            if (res && res.folder) {
                toast.success(`Photo moved to ${toAlbum}!`);
                setActiveFolder(res.folder);
            } else {
                toast.error(res?.message || 'Failed to move photo');
            }
        } catch (err) {
            toast.error('Failed to move photo');
        }
    };

    const handleSaveVariant = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!activeFolder || !editingVariant) return;
        setSavingVariant(true);
        try {
            const res = await api.updateMediaFolderItem(token || undefined, activeFolder.folderNumber, {
                itemType: 'floor-variant',
                itemId: editingVariant.itemId,
                filePath: editingVariant.url,
                title: editingVariant.title,
                unitType: editingVariant.unitType || '2 BHK',
                priceRange: editingVariant.priceRange || 'On Request',
                subUnitVariant: editingVariant.subUnitVariant,
                variantType: editingVariant.variantType
            });
            if (res && res.folder) {
                toast.success('Floor variant updated!');
                setActiveFolder(res.folder);
                setEditingVariant(null);
            } else {
                toast.error(res?.message || 'Failed to update variant');
            }
        } catch (err) {
            toast.error('Error updating floor variant');
        } finally {
            setSavingVariant(false);
        }
    };

    const handleCreateUnitTypeTab = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = newUnitTypeName.trim();
        if (!trimmed) {
            toast.warning('Please enter a Unit Type name e.g. 1 BHK, 4 BHK');
            return;
        }
        if (!customUnitTabs.includes(trimmed)) {
            setCustomUnitTabs(prev => [...prev, trimmed]);
        }
        setSelectedUnitTypeTab(trimmed);
        setUploadUnitType(trimmed);
        setUploadPriceRange(newUnitTypePrice || 'On Request');
        setIsAddUnitTypeModalOpen(false);
        setNewUnitTypeName('');
        setNewUnitTypePrice('On Request');
        toast.success(`Unit Type tab "${trimmed}" created!`);
    };

    const handleUpdateUnitTypePrice = async (uType: string, newPrice: string) => {
        if (!activeFolder || !uType) return;
        try {
            const res = await api.updateMediaFolderItem(token || undefined, activeFolder.folderNumber, {
                itemType: 'unit-type-price',
                unitType: uType,
                priceRange: newPrice
            });
            if (res && res.folder) {
                setActiveFolder(res.folder);
                toast.success(`Price range updated for ${uType}!`);
            }
        } catch (e) {
            toast.error('Failed to update price range');
        }
    };

    const handleSaveDoc = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!activeFolder || !editingDoc) return;
        setSavingDoc(true);
        try {
            const res = await api.updateMediaFolderItem(token || undefined, activeFolder.folderNumber, {
                itemType: 'document',
                itemId: editingDoc.itemId,
                filePath: editingDoc.url,
                title: editingDoc.title
            });
            if (res && res.folder) {
                toast.success('Document updated!');
                setActiveFolder(res.folder);
                setEditingDoc(null);
            } else {
                toast.error(res?.message || 'Failed to update document');
            }
        } catch (err) {
            toast.error('Error updating document');
        } finally {
            setSavingDoc(false);
        }
    };

    const handleDownloadSampleZip = async () => {
        try {
            toast.info('Downloading sample-project-media-folder.zip...');
            await api.downloadSampleZip();
            toast.success('Sample ZIP downloaded successfully!');
        } catch (err: any) {
            console.error('Download sample zip error:', err);
            toast.error(err.message || 'Failed to download sample ZIP');
        }
    };

    const handleDownloadFolderBackup = async (folderNum: number, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setDownloadingBackup(folderNum);
        try {
            toast.info(`Preparing backup ZIP for Folder #${folderNum}...`);
            await api.downloadFolderBackupZip(folderNum, token || undefined);
            toast.success(`Backup for Folder #${folderNum} downloaded!`);
        } catch (err: any) {
            toast.error(err.message || 'Failed to download backup');
        } finally {
            setDownloadingBackup(null);
        }
    };

    const handleDownloadAllBackup = async () => {
        setDownloadingBackup('all');
        try {
            toast.info('Preparing master backup of all media folders...');
            await api.downloadAllFoldersBackupZip(token || undefined);
            toast.success('All Media Folders backup downloaded!');
        } catch (err: any) {
            toast.error(err.message || 'Failed to download backup of all folders');
        } finally {
            setDownloadingBackup(null);
        }
    };

    const handleOpenZipUploadModal = (folderNum?: number) => {
        if (folderNum) {
            setZipTargetMode('existing');
            setZipSelectedFolder(folderNum);
        } else {
            setZipTargetMode('new');
            const nums = folders.map(f => Number(f.folderNumber)).filter(n => !isNaN(n));
            const nextNum = nums.length > 0 ? Math.max(...nums) + 1 : 1;
            setZipNewFolderNumber(nextNum);
            setZipNewFolderName(`Project ${nextNum}`);
            if (folders.length > 0) setZipSelectedFolder(folders[0].folderNumber);
        }
        setZipFile(null);
        setIsZipModalOpen(true);
    };

    const handleZipUploadSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (zipTargetMode === 'bulk') {
            const filesToUpload = zipFiles.length > 0 ? zipFiles : (zipFile ? [zipFile] : []);
            if (!filesToUpload.length) {
                toast.warning('Please select one or more .zip files');
                return;
            }

            setZipUploading(true);
            try {
                const res = await api.uploadBulkMediaFolderZip(token || undefined, filesToUpload);
                if (res && (res.folders || res.message)) {
                    toast.success(res.message || `Processed ${res.folders?.length || 0} media folders!`);
                    if (res.errors && Array.isArray(res.errors)) {
                        res.errors.forEach((errStr: string) => toast.warning(errStr));
                    }
                    setIsZipModalOpen(false);
                    setZipFiles([]);
                    setZipFile(null);
                    await fetchFolders();
                } else {
                    toast.error(res?.message || res?.error || 'Failed to process bulk ZIP files');
                }
            } catch (err: any) {
                console.error('Bulk ZIP upload error:', err);
                toast.error(err.message || 'Error processing bulk ZIP upload');
            } finally {
                setZipUploading(false);
            }
            return;
        }

        if (!zipFile) {
            toast.warning('Please select a .zip file to upload');
            return;
        }

        setZipUploading(true);
        try {
            if (zipTargetMode === 'new') {
                const res = await api.createMediaFolderFromZip(token || undefined, zipFile, {
                    folderNumber: zipNewFolderNumber || undefined,
                    folderName: zipNewFolderName || undefined,
                    description: zipNewFolderDesc || undefined
                });

                if (res && res.multiple) {
                    toast.success(res.message || `Created ${res.folders?.length || 0} media folders from ZIP!`);
                    setIsZipModalOpen(false);
                    setZipFile(null);
                    await fetchFolders();
                } else if (res && res.folder && res.folder.folderNumber) {
                    toast.success(res.message || `Media Folder #${res.folder.folderNumber} created from ZIP!`);
                    setIsZipModalOpen(false);
                    setZipFile(null);
                    await fetchFolders();
                    handleOpenFolder(res.folder.folderNumber);
                } else {
                    toast.error(res?.message || res?.error || 'Failed to create folder from ZIP');
                }
            } else {
                // Existing folder
                const targetNum = Number(zipSelectedFolder);
                if (!targetNum || isNaN(targetNum)) {
                    toast.warning('Please select a valid folder');
                    setZipUploading(false);
                    return;
                }

                const res = await api.uploadMediaFolderZip(token || undefined, targetNum, zipFile);
                if (res && res.folder) {
                    toast.success(res.message || `ZIP extracted into Folder #${targetNum}!`);
                    setIsZipModalOpen(false);
                    setZipFile(null);
                    await fetchFolders();
                    if (activeFolder?.folderNumber === targetNum) {
                        fetchFolderDetail(targetNum);
                    } else {
                        handleOpenFolder(targetNum);
                    }
                } else {
                    toast.error(res?.message || res?.error || 'Failed to upload ZIP into folder');
                }
            }
        } catch (err: any) {
            console.error('ZIP upload error:', err);
            toast.error(err.message || 'Error uploading ZIP');
        } finally {
            setZipUploading(false);
        }
    };

    const handleOpenCreateModal = () => {
        if (folders.length > 0) {
            const nums = folders.map(f => Number(f.folderNumber)).filter(n => !isNaN(n));
            const maxNum = nums.length > 0 ? Math.max(...nums) : 0;
            setNewFolderNumber(maxNum + 1);
        } else {
            setNewFolderNumber(1);
        }
        setIsCreateModalOpen(true);
    };

    const fetchFolders = async () => {
        setLoading(true);
        try {
            const data = await api.getMediaFolders(token || undefined);
            if (Array.isArray(data)) {
                setFolders(data);
                // Suggest next folder number
                if (data.length > 0) {
                    const maxNum = Math.max(...data.map(f => f.folderNumber || 0));
                    setNewFolderNumber(maxNum + 1);
                } else {
                    setNewFolderNumber(1);
                }
            }
        } catch (err) {
            console.error('Failed to fetch media folders:', err);
            toast.error('Failed to load media folders');
        } finally {
            setLoading(false);
        }
    };

    const fetchFolderDetail = async (folderNum: number) => {
        setLoadingDetail(true);
        try {
            const data = await api.getMediaFolder(folderNum, token || undefined);
            if (data && data.folderNumber) {
                setActiveFolder(data);
            } else {
                toast.error(data.message || 'Folder not found');
                setActiveFolder(null);
            }
        } catch (err) {
            console.error('Failed to load folder detail:', err);
            toast.error('Failed to load folder details');
        } finally {
            setLoadingDetail(false);
        }
    };

    useEffect(() => {
        fetchFolders();
    }, [token]);

    useEffect(() => {
        if (activeFolderParam) {
            const num = parseInt(activeFolderParam);
            if (!isNaN(num)) {
                fetchFolderDetail(num);
            }
        } else {
            setActiveFolder(null);
        }
        setSelectedFiles([]);
    }, [activeFolderParam, token]);

    const handleOpenFolder = (folderNum: number) => {
        setSearchParams({ folder: String(folderNum) });
    };

    const handleBackToList = () => {
        setSearchParams({});
        setActiveFolder(null);
        setSelectedFiles([]);
        fetchFolders();
    };

    const handleCreateFolder = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreating(true);
        try {
            const folderNumToSend = newFolderNumber ? Number(newFolderNumber) : undefined;
            const res = await api.createMediaFolder(token || undefined, {
                folderNumber: folderNumToSend,
                folderName: newFolderName || (folderNumToSend ? `Folder #${folderNumToSend}` : ''),
                description: newFolderDesc
            });

            if (res && res.folderNumber) {
                toast.success(`Media Folder #${res.folderNumber} created!`);
                setIsCreateModalOpen(false);
                setNewFolderName('');
                setNewFolderDesc('');
                await fetchFolders();
                handleOpenFolder(res.folderNumber);
            } else {
                toast.error(res?.message || res?.error || 'Failed to create folder');
            }
        } catch (err: any) {
            console.error('Error creating folder:', err);
            toast.error(err.message || 'Error creating folder');
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteFolder = (folderNum: number, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setDeleteDialog({
            type: 'folder',
            folderNumber: folderNum,
            title: `Delete Folder #${folderNum}`,
            message: `Are you sure you want to delete Media Folder #${folderNum}?`,
            subMessage: 'All uploaded floor variants, gallery photos, and documents inside this folder will be permanently deleted from the disk and database. This action cannot be undone.'
        });
    };

    // Folder selection helpers
    const handleToggleSelectFolder = (folderNum: number, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setSelectedFolders(prev =>
            prev.includes(folderNum) ? prev.filter(n => n !== folderNum) : [...prev, folderNum]
        );
    };

    const handleSelectAllFolders = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedFolders(filteredFolders.map(f => f.folderNumber));
        } else {
            setSelectedFolders([]);
        }
    };

    const handlePromptBulkDeleteFolders = () => {
        if (!selectedFolders.length) return;
        setDeleteDialog({
            type: 'bulk-folders',
            folderNumbers: selectedFolders,
            title: `Delete ${selectedFolders.length} Media Folder${selectedFolders.length > 1 ? 's' : ''}`,
            message: `Are you sure you want to permanently delete the ${selectedFolders.length} selected media folder${selectedFolders.length > 1 ? 's' : ''}?`,
            subMessage: `Selected Folders: #${selectedFolders.join(', #')}. All uploaded floor variants, gallery photos, and documents inside these folders will be permanently deleted from the disk and database.`
        });
    };

    // File selection helpers inside active folder
    const isFileSelected = (filePath: string) => {
        return selectedFiles.some(f => f.filePath === filePath);
    };

    const handleToggleSelectFile = (fileItem: { subFolder: string; filePath: string; fileId?: string }, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        setSelectedFiles(prev => {
            const exists = prev.some(f => f.filePath === fileItem.filePath);
            if (exists) {
                return prev.filter(f => f.filePath !== fileItem.filePath);
            } else {
                return [...prev, fileItem];
            }
        });
    };

    const areAllFilesSelected = (filesToCheck: Array<{ filePath: string }>) => {
        if (!filesToCheck.length) return false;
        return filesToCheck.every(item => selectedFiles.some(f => f.filePath === item.filePath));
    };

    const handleToggleSelectAllFiles = (filesToToggle: Array<{ subFolder: string; filePath: string; fileId?: string }>) => {
        if (areAllFilesSelected(filesToToggle)) {
            const pathsToRemove = new Set(filesToToggle.map(f => f.filePath));
            setSelectedFiles(prev => prev.filter(f => !pathsToRemove.has(f.filePath)));
        } else {
            setSelectedFiles(prev => {
                const existingPaths = new Set(prev.map(f => f.filePath));
                const toAdd = filesToToggle.filter(f => !existingPaths.has(f.filePath));
                return [...prev, ...toAdd];
            });
        }
    };

    const handlePromptBulkDeleteFiles = () => {
        if (!selectedFiles.length || !activeFolder) return;
        setDeleteDialog({
            type: 'bulk-files',
            filesList: selectedFiles,
            title: `Delete ${selectedFiles.length} Selected File${selectedFiles.length > 1 ? 's' : ''}`,
            message: `Are you sure you want to delete ${selectedFiles.length} selected file${selectedFiles.length > 1 ? 's' : ''} from Folder #${activeFolder.folderNumber}?`,
            subMessage: 'These files will be permanently deleted from the disk and database.'
        });
    };

    const handleConfirmDelete = async () => {
        if (!deleteDialog) return;
        setDeleting(true);
        try {
            if (deleteDialog.type === 'folder' && deleteDialog.folderNumber) {
                const folderNum = deleteDialog.folderNumber;
                const res = await api.deleteMediaFolder(token || undefined, folderNum);
                toast.success(res?.message || `Folder #${folderNum} deleted`);
                setSelectedFolders(prev => prev.filter(n => n !== folderNum));
                setDeleteDialog(null);
                if (activeFolder?.folderNumber === folderNum) {
                    handleBackToList();
                } else {
                    fetchFolders();
                }
            } else if (deleteDialog.type === 'bulk-folders' && deleteDialog.folderNumbers?.length) {
                const fNums = deleteDialog.folderNumbers;
                const res = await api.bulkDeleteMediaFolders(token || undefined, fNums);
                toast.success(res?.message || `${fNums.length} folders deleted successfully`);
                setSelectedFolders([]);
                setDeleteDialog(null);
                if (activeFolder && fNums.includes(activeFolder.folderNumber)) {
                    handleBackToList();
                } else {
                    fetchFolders();
                }
            } else if (deleteDialog.type === 'file' && deleteDialog.fileParams && activeFolder) {
                const res = await api.deleteMediaFolderFile(token!, activeFolder.folderNumber, deleteDialog.fileParams);
                if (res && res.folder) {
                    toast.success('File deleted');
                    setActiveFolder(res.folder);
                    setSelectedFiles(prev => prev.filter(f => f.filePath !== deleteDialog.fileParams?.filePath));
                }
                setDeleteDialog(null);
            } else if (deleteDialog.type === 'bulk-files' && deleteDialog.filesList?.length && activeFolder) {
                const res = await api.bulkDeleteMediaFolderFiles(token || undefined, activeFolder.folderNumber, deleteDialog.filesList);
                if (res && res.folder) {
                    toast.success(res?.message || `${deleteDialog.filesList.length} files deleted successfully`);
                    setActiveFolder(res.folder);
                    setSelectedFiles([]);
                }
                setDeleteDialog(null);
            }
        } catch (err: any) {
            toast.error(err?.message || 'Failed to delete');
        } finally {
            setDeleting(false);
        }
    };

    const handleSyncFolder = async (folderNum: number) => {
        try {
            const res = await api.syncMediaFolder(token!, folderNum);
            toast.success(res.message || 'Folder synced from disk');
            fetchFolderDetail(folderNum);
            fetchFolders();
        } catch (err) {
            toast.error('Failed to sync folder from disk');
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, subFolder: string) => {
        const files = Array.from(e.target.files || []);
        if (!files.length || !activeFolder) return;

        setUploading(true);
        try {
            const extraData: Record<string, string> = {};
            if (subFolder === 'floor-variants') {
                if (variantLabel) extraData.subUnitVariant = variantLabel;
                if (variantType) extraData.variantType = variantType;
                extraData.unitType = uploadUnitType || '2 BHK';
                extraData.priceRange = uploadPriceRange || 'On Request';
            } else if (subFolder === 'documents' && docTitle) {
                extraData.title = docTitle;
            }

            const res = await api.uploadMediaFolderFiles(token!, activeFolder.folderNumber, subFolder, files, extraData);
            if (res && res.folder) {
                toast.success(res.message || 'Upload successful');
                setActiveFolder(res.folder);
                setVariantLabel('');
                setDocTitle('');
            } else {
                toast.error(res.message || 'Failed to upload files');
            }
        } catch (err: any) {
            toast.error(err.message || 'Upload error');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleDeleteFile = (subFolder: string, filePath: string, fileId?: string) => {
        const filename = filePath.split('/').pop() || 'this file';
        setDeleteDialog({
            type: 'file',
            fileParams: { subFolder, filePath, fileId },
            title: 'Delete File',
            message: `Are you sure you want to delete this file?`,
            subMessage: filename
        });
    };

    const filteredFolders = folders.filter(f => 
        String(f.folderNumber).includes(searchQuery) ||
        f.folderName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <AdminLayout>
            <div className="p-4 md:p-8 space-y-6 max-w-7xl mx-auto">
                {/* TOP HEADER */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-[#05144b] to-[#4c1d95] text-white flex items-center justify-center text-xl shadow-md shadow-indigo-950/20">
                                <i className="fa-solid fa-photo-film"></i>
                            </div>
                            <div>
                                <h1 className="text-2xl font-black text-[#05144b] tracking-tight">Project Media Folders</h1>
                                <p className="text-xs text-slate-500 font-medium">Manage project image folders by number and auto-import into Add Project or Excel</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                        {activeFolder ? (
                            <>
                                <button
                                    onClick={handleBackToList}
                                    className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs flex items-center gap-2 transition-all shadow-sm"
                                >
                                    <i className="fa-solid fa-arrow-left"></i> All Folders
                                </button>

                                <button
                                    onClick={() => handleOpenEditFolderModal(activeFolder)}
                                    className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 font-extrabold text-xs flex items-center gap-2 transition-all shadow-sm"
                                    title="Edit Folder Name & Number"
                                >
                                    <i className="fa-solid fa-pen text-slate-600"></i> Edit Details
                                </button>

                                <button
                                    onClick={(e) => handleDeleteFolder(activeFolder.folderNumber, e)}
                                    className="px-4 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 font-extrabold text-xs flex items-center gap-2 transition-all shadow-sm"
                                    title="Delete this entire folder and all its assets"
                                >
                                    <i className="fa-solid fa-trash-can text-rose-600"></i> Delete Folder
                                </button>

                                <button
                                    onClick={() => handleSyncFolder(activeFolder.folderNumber)}
                                    title="Scan disk folder for any newly added files"
                                    className="px-4 py-2.5 rounded-xl border border-blue-200 bg-blue-50/50 hover:bg-blue-100/50 text-blue-700 font-bold text-xs flex items-center gap-2 transition-all shadow-sm"
                                >
                                    <i className="fa-solid fa-arrows-rotate"></i> Sync Disk
                                </button>

                                <button
                                    onClick={() => handleOpenZipUploadModal(activeFolder.folderNumber)}
                                    className="px-4 py-2.5 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-900 font-extrabold text-xs flex items-center gap-2 hover:bg-indigo-100 transition-all shadow-sm"
                                    title="Upload and extract ZIP into this folder"
                                >
                                    <i className="fa-solid fa-file-arrow-up text-indigo-600"></i> Upload ZIP
                                </button>

                                <button
                                    onClick={handleDownloadSampleZip}
                                    className="px-4 py-2.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 text-emerald-900 font-extrabold text-xs flex items-center gap-2 transition-all shadow-sm"
                                    title="Download sample pre-structured ZIP folder with 2D, 3D & Info floor plans"
                                >
                                    <i className="fa-solid fa-file-zipper text-emerald-600"></i> Sample ZIP
                                </button>

                                <button
                                    onClick={() => handleDownloadFolderBackup(activeFolder.folderNumber)}
                                    disabled={downloadingBackup === activeFolder.folderNumber}
                                    className="px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 font-extrabold text-xs flex items-center gap-2 hover:bg-slate-100 transition-all shadow-sm disabled:opacity-60"
                                    title="Download all files of this folder as a ZIP archive"
                                >
                                    {downloadingBackup === activeFolder.folderNumber ? (
                                        <i className="fa-solid fa-spinner animate-spin text-slate-600"></i>
                                    ) : (
                                        <i className="fa-solid fa-box-archive text-slate-600"></i>
                                    )}
                                    Backup ZIP
                                </button>

                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(String(activeFolder.folderNumber));
                                        toast.info(`Folder #${activeFolder.folderNumber} copied to clipboard!`);
                                    }}
                                    className="px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 font-extrabold text-xs flex items-center gap-2 transition-all shadow-sm"
                                >
                                    <i className="fa-regular fa-copy"></i> Copy #{activeFolder.folderNumber}
                                </button>

                                <Link
                                    to={`/admin-add-project?mediaFolder=${activeFolder.folderNumber}`}
                                    className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white font-extrabold text-xs flex items-center gap-2 hover:opacity-95 shadow-md shadow-indigo-950/20"
                                >
                                    <i className="fa-solid fa-plus-circle"></i> Use in Add Project
                                </Link>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={handleDownloadSampleZip}
                                    className="px-4 py-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 text-emerald-900 font-black text-xs uppercase tracking-wider flex items-center gap-2 transition-all shadow-sm"
                                    title="Download sample pre-structured ZIP folder with instructions"
                                >
                                    <i className="fa-solid fa-file-zipper text-emerald-600 text-sm"></i> Sample ZIP
                                </button>

                                <button
                                    onClick={() => handleOpenZipUploadModal()}
                                    className="px-4 py-3 rounded-xl bg-indigo-50 hover:bg-indigo-100 border border-indigo-300 text-indigo-900 font-black text-xs uppercase tracking-wider flex items-center gap-2 transition-all shadow-sm"
                                    title="Upload a ZIP file to create or populate media folders"
                                >
                                    <i className="fa-solid fa-cloud-arrow-up text-indigo-600 text-sm"></i> Upload ZIP
                                </button>

                                {folders.length > 0 && (
                                    <button
                                        onClick={handleDownloadAllBackup}
                                        disabled={downloadingBackup === 'all'}
                                        className="px-4 py-3 rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-900 font-black text-xs uppercase tracking-wider flex items-center gap-2 transition-all shadow-sm disabled:opacity-60"
                                        title="Download full backup of all media folders as a single ZIP archive"
                                    >
                                        {downloadingBackup === 'all' ? (
                                            <i className="fa-solid fa-spinner animate-spin text-amber-600"></i>
                                        ) : (
                                            <i className="fa-solid fa-box-archive text-amber-600 text-sm"></i>
                                        )}
                                        Backup All
                                    </button>
                                )}

                                <button
                                    onClick={handleOpenCreateModal}
                                    className="px-5 py-3 rounded-xl bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white font-black text-xs uppercase tracking-wider flex items-center gap-2.5 hover:shadow-lg hover:shadow-indigo-950/20 hover:-translate-y-0.5 transition-all"
                                >
                                    <i className="fa-solid fa-folder-plus text-sm"></i> Create Media Folder
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* MAIN CONTENT AREA */}
                {!activeFolder ? (
                    /* FOLDERS GRID VIEW */
                    <div className="space-y-4">
                        {/* SEARCH & FILTERS */}
                        <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                            <div className="flex items-center gap-3 flex-1 min-w-[280px]">
                                <div className="relative flex-1 max-w-md">
                                    <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                                    <input
                                        type="text"
                                        placeholder="Search by folder # or project name..."
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#05144b]/20"
                                    />
                                </div>

                                {filteredFolders.length > 0 && (
                                    <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-slate-700 hover:text-indigo-600 bg-slate-50 hover:bg-slate-100 px-3 py-2 rounded-xl border border-slate-200 transition shrink-0">
                                        <input
                                            type="checkbox"
                                            checked={filteredFolders.length > 0 && selectedFolders.length === filteredFolders.length}
                                            onChange={handleSelectAllFolders}
                                            className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 cursor-pointer accent-rose-600"
                                        />
                                        <span>Select All ({filteredFolders.length})</span>
                                    </label>
                                )}
                            </div>

                            <div className="flex items-center gap-3">
                                {selectedFolders.length > 0 && (
                                    <div className="flex items-center gap-2 animate-in fade-in duration-200">
                                        <span className="text-xs font-black text-rose-700 bg-rose-50 border border-rose-200 px-3 py-1.5 rounded-xl">
                                            {selectedFolders.length} selected
                                        </span>
                                        <button
                                            type="button"
                                            onClick={handlePromptBulkDeleteFolders}
                                            className="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm shadow-rose-600/30 transition active:scale-95"
                                        >
                                            <i className="fa-solid fa-trash-can text-xs"></i>
                                            <span>Delete Selected ({selectedFolders.length})</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedFolders([])}
                                            className="px-2.5 py-1.5 text-slate-500 hover:text-slate-800 text-xs font-bold hover:bg-slate-100 rounded-xl transition"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                )}
                                <div className="text-xs font-bold text-slate-500">
                                    Total Folders: <span className="text-[#05144b] font-black">{filteredFolders.length}</span>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
                                <div className="w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                <p className="text-xs font-bold text-slate-500">Loading media folders...</p>
                            </div>
                        ) : filteredFolders.length === 0 ? (
                            <div className="text-center py-20 bg-white rounded-2xl border border-slate-100 p-8">
                                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400 text-2xl">
                                    <i className="fa-solid fa-folder-open"></i>
                                </div>
                                <h3 className="text-base font-bold text-slate-800 mb-1">No Media Folders Found</h3>
                                <p className="text-xs text-slate-500 max-w-sm mx-auto mb-5">
                                    Create a numbered folder to start organizing Floor Variants, Gallery photos, and Documents for your projects.
                                </p>
                                <button
                                    onClick={handleOpenCreateModal}
                                    className="px-4 py-2 bg-[#05144b] text-white rounded-xl text-xs font-bold inline-flex items-center gap-2 hover:bg-[#4c1d95] transition-all"
                                >
                                    <i className="fa-solid fa-plus"></i> Create Folder #1
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                                {filteredFolders.map(folder => (
                                    <div
                                        key={folder._id}
                                        onClick={() => handleOpenFolder(folder.folderNumber)}
                                        className={`group bg-white rounded-2xl border ${
                                            selectedFolders.includes(folder.folderNumber)
                                                ? 'ring-2 ring-rose-500 border-rose-400 shadow-lg'
                                                : 'border-slate-200 hover:border-indigo-400 shadow-sm hover:shadow-xl'
                                        } hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col justify-between`}
                                    >
                                        <div>
                                            {/* PREVIEW THUMBNAIL */}
                                            <div className="h-48 bg-slate-900 relative overflow-hidden flex items-center justify-center">
                                                {folder.previewThumbnail ? (
                                                    <img
                                                        src={getImageUrl(folder.previewThumbnail)}
                                                        alt={folder.folderName}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gradient-to-br from-slate-800 via-indigo-950 to-slate-900 flex flex-col items-center justify-center text-slate-400 p-4">
                                                        <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 flex items-center justify-center text-2xl text-amber-400 mb-2 shadow-inner">
                                                            <i className="fa-solid fa-folder-open"></i>
                                                        </div>
                                                        <span className="text-[11px] font-black uppercase tracking-wider text-slate-300">Empty Media Folder</span>
                                                    </div>
                                                )}

                                                {/* GRADIENT OVERLAYS */}
                                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-black/60 pointer-events-none"></div>

                                                {/* TOP HEADER BAR */}
                                                <div className="absolute inset-x-0 top-0 p-3 flex items-center justify-between gap-2 z-10">
                                                    <div className="flex items-center gap-2">
                                                        {/* CHECKBOX */}
                                                        <div
                                                            onClick={(e) => handleToggleSelectFolder(folder.folderNumber, e)}
                                                            className={`w-7 h-7 rounded-xl backdrop-blur-md flex items-center justify-center transition-all cursor-pointer border ${
                                                                selectedFolders.includes(folder.folderNumber)
                                                                    ? 'bg-rose-600 border-white text-white shadow-md'
                                                                    : 'bg-black/50 hover:bg-black/70 border-white/30 text-white'
                                                            }`}
                                                            title={selectedFolders.includes(folder.folderNumber) ? "Deselect folder" : "Select folder for deletion"}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedFolders.includes(folder.folderNumber)}
                                                                onChange={() => {}}
                                                                className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 cursor-pointer accent-rose-600 pointer-events-none"
                                                            />
                                                        </div>

                                                        {/* FOLDER NUMBER BADGE */}
                                                        <div className="bg-[#05144b]/90 backdrop-blur-md text-white px-3 py-1.5 rounded-xl text-xs font-black shadow-lg flex items-center gap-2 border border-white/20">
                                                            <i className="fa-solid fa-folder-closed text-amber-400 text-xs"></i>
                                                            <span>Folder #{folder.folderNumber}</span>
                                                        </div>
                                                    </div>

                                                    {/* QUICK ACTIONS BUTTONS */}
                                                    <div className="flex items-center gap-1.5">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => handleOpenEditFolderModal(folder, e)}
                                                            className="w-8 h-8 rounded-xl bg-white/95 hover:bg-blue-600 text-slate-700 hover:text-white backdrop-blur-md shadow-md flex items-center justify-center transition-all duration-200 border border-white/50 active:scale-95"
                                                            title="Edit Folder Name & Number"
                                                        >
                                                            <i className="fa-solid fa-pen text-[11px]"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => handleDeleteFolder(folder.folderNumber, e)}
                                                            className="w-8 h-8 rounded-xl bg-white/95 hover:bg-rose-600 text-slate-700 hover:text-white backdrop-blur-md shadow-md flex items-center justify-center transition-all duration-200 border border-white/50 active:scale-95"
                                                            title="Delete Folder"
                                                        >
                                                            <i className="fa-solid fa-trash-can text-[11px]"></i>
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* BOTTOM THUMBNAIL INFO BADGE */}
                                                <div className="absolute bottom-2.5 left-3 right-3 flex items-center justify-between z-10 pointer-events-none">
                                                    <span className="text-[11px] font-bold text-white/90 drop-shadow flex items-center gap-1.5">
                                                        <i className="fa-regular fa-clock text-[10px] text-amber-300"></i>
                                                        {new Date(folder.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </span>
                                                    <span className="text-[11px] font-black text-amber-300 drop-shadow bg-black/40 backdrop-blur-xs px-2 py-0.5 rounded-lg border border-white/10">
                                                        {folder.totalFiles || (folder.floorCount + folder.galleryCount + folder.docCount)} Files
                                                    </span>
                                                </div>
                                            </div>

                                            {/* BODY */}
                                            <div className="p-4 space-y-3">
                                                <div>
                                                    <h3 className="font-extrabold text-base text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-1">
                                                        {folder.folderName || `Folder #${folder.folderNumber}`}
                                                    </h3>
                                                    <p className="text-xs text-slate-500 line-clamp-2 min-h-[32px] leading-relaxed mt-1">
                                                        {folder.description || 'No description provided for this media folder.'}
                                                    </p>
                                                </div>

                                                {/* STATS PILLS (3 TILES) */}
                                                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-center">
                                                    <div className="bg-amber-50/80 hover:bg-amber-50 rounded-xl p-2 border border-amber-200/70 transition-colors">
                                                        <div className="flex items-center justify-center gap-1.5 text-amber-900 font-black text-sm">
                                                            <i className="fa-solid fa-layer-group text-xs text-amber-600"></i>
                                                            <span>{folder.floorCount}</span>
                                                        </div>
                                                        <div className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mt-0.5">Floors</div>
                                                    </div>
                                                    <div className="bg-blue-50/80 hover:bg-blue-50 rounded-xl p-2 border border-blue-200/70 transition-colors">
                                                        <div className="flex items-center justify-center gap-1.5 text-blue-900 font-black text-sm">
                                                            <i className="fa-solid fa-images text-xs text-blue-600"></i>
                                                            <span>{folder.galleryCount}</span>
                                                        </div>
                                                        <div className="text-[10px] font-bold text-blue-700 uppercase tracking-wider mt-0.5">Gallery</div>
                                                    </div>
                                                    <div className="bg-emerald-50/80 hover:bg-emerald-50 rounded-xl p-2 border border-emerald-200/70 transition-colors">
                                                        <div className="flex items-center justify-center gap-1.5 text-emerald-900 font-black text-sm">
                                                            <i className="fa-solid fa-file-shield text-xs text-emerald-600"></i>
                                                            <span>{folder.docCount}</span>
                                                        </div>
                                                        <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mt-0.5">Docs</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* CARD FOOTER */}
                                        <div className="p-3 px-4 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between gap-2 rounded-b-2xl">
                                            <span className="text-[11px] font-bold text-slate-500 flex items-center gap-1.5">
                                                <i className="fa-solid fa-folder-open text-indigo-500 text-xs"></i>
                                                <span>Folder #{folder.folderNumber}</span>
                                            </span>

                                            {/* PRIMARY OPEN BUTTON */}
                                            <button
                                                type="button"
                                                onClick={() => handleOpenFolder(folder.folderNumber)}
                                                className="h-8 px-4 rounded-xl bg-gradient-to-r from-[#05144b] to-[#4c1d95] hover:opacity-95 text-white font-extrabold text-xs flex items-center gap-1.5 shadow-sm transition-all group-hover:shadow-indigo-950/20 active:scale-95"
                                            >
                                                <span>Open Folder</span>
                                                <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-0.5 transition-transform"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    /* FOLDER DETAIL / WORKSPACE VIEW */
                    <div className="space-y-6">
                        {loadingDetail ? (
                            <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
                                <div className="w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                <p className="text-xs font-bold text-slate-500">Loading folder assets...</p>
                            </div>
                        ) : (
                            <>
                                {/* FOLDER WORKSPACE HEADER */}
                                <div className="bg-gradient-to-r from-[#05144b] to-[#251052] rounded-2xl p-6 text-white shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <span className="bg-[#f1ae24] text-black px-3 py-1 rounded-full text-xs font-black tracking-wide">
                                                FOLDER #{activeFolder.folderNumber}
                                            </span>
                                            <span className="text-xs text-white/60">
                                                Created: {new Date(activeFolder.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <h2 className="text-2xl font-black">{activeFolder.folderName}</h2>
                                        {activeFolder.description && (
                                            <p className="text-xs text-white/70 max-w-xl">{activeFolder.description}</p>
                                        )}
                                    </div>

                                    <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/20 text-xs space-y-1">
                                        <div className="font-bold text-amber-300">
                                            <i className="fa-solid fa-circle-info mr-1"></i> How to use this folder:
                                        </div>
                                        <div className="text-white/80">
                                            In <b>Add Project</b> or <b>Excel Import</b>, simply enter <b>{activeFolder.folderNumber}</b> in the <b>Media Folder #</b> field to load all these images instantly!
                                        </div>
                                    </div>
                                </div>

                                {/* TABS NAVIGATION */}
                                <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
                                    {[
                                        { id: 'floorVariants', label: 'Floor Variants', icon: 'fa-layer-group', count: activeFolder.floorVariants?.length || 0 },
                                        { id: 'gallery', label: 'Gallery Albums', icon: 'fa-images', count: Object.values(activeFolder.gallery || {}).reduce((acc, a) => acc + (a?.length || 0), 0) },
                                        { id: 'documents', label: 'Documents & TP', icon: 'fa-file-shield', count: (activeFolder.documents?.legalDocuments?.length || 0) + (activeFolder.documents?.brochure ? 1 : 0) + (activeFolder.documents?.tpImage ? 1 : 0) },
                                        { id: 'cover', label: 'Cover Photo', icon: 'fa-image', count: activeFolder.coverImage ? 1 : 0 },
                                    ].map(tab => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id as any)}
                                            className={`px-5 py-3 rounded-xl font-bold text-xs flex items-center gap-2.5 transition-all ${
                                                activeTab === tab.id
                                                    ? 'bg-[#05144b] text-white shadow-md'
                                                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                                            }`}
                                        >
                                            <i className={`fa-solid ${tab.icon}`}></i>
                                            <span>{tab.label}</span>
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                                                activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                                            }`}>
                                                {tab.count}
                                            </span>
                                        </button>
                                    ))}
                                </div>

                                {/* TAB 1: FLOOR VARIANTS */}
                                {activeTab === 'floorVariants' && (() => {
                                    const detectedUnitTypes: string[] = Array.from(new Set([
                                        ...(activeFolder.floorVariants || []).map(fv => fv.unitType?.trim() || '').filter(Boolean),
                                        ...customUnitTabs
                                    ])).filter((t): t is string => Boolean(t));
                                    const availableUnitTabs: string[] = detectedUnitTypes.length > 0 ? detectedUnitTypes : ['2 BHK', '3 BHK', '4 BHK'];
                                    
                                    const filteredVariants = selectedUnitTypeTab === 'All'
                                        ? activeFolder.floorVariants
                                        : activeFolder.floorVariants.filter(fv => (fv.unitType || '2 BHK').toLowerCase() === selectedUnitTypeTab.toLowerCase());

                                    return (
                                        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
                                            {/* HEADER & UPLOAD CONTROLS */}
                                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-slate-100">
                                                <div>
                                                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                                                        <span>Floor Variants (2D & 3D Plans)</span>
                                                        <span className="bg-indigo-100 text-indigo-800 text-[10px] px-2.5 py-0.5 rounded-full font-black">
                                                            {activeFolder.floorVariants.length} Total Plans
                                                        </span>
                                                    </h3>
                                                    <p className="text-xs text-slate-500">Organize floor plans into unit tabs (2 BHK, 3 BHK, etc.) with 2D & 3D variants</p>
                                                </div>

                                                {/* UPLOAD FORM */}
                                                <div className="flex flex-wrap items-center gap-2 bg-slate-50 p-2.5 rounded-2xl border border-slate-200/80">
                                                    {/* Unit Type Selector */}
                                                    <div className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-xl border border-slate-200">
                                                        <span className="text-[10px] font-black text-slate-400 uppercase">Tab:</span>
                                                        <select
                                                            value={uploadUnitType}
                                                            onChange={e => {
                                                                setUploadUnitType(e.target.value);
                                                                if (selectedUnitTypeTab !== 'All') {
                                                                    setSelectedUnitTypeTab(e.target.value);
                                                                }
                                                            }}
                                                            className="text-xs font-bold text-[#05144b] bg-transparent focus:outline-none cursor-pointer"
                                                        >
                                                            {availableUnitTabs.map(u => (
                                                                <option key={u} value={u}>{u}</option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <input
                                                        type="text"
                                                        placeholder="Variant (e.g. Type A, Luxury)"
                                                        value={variantLabel}
                                                        onChange={e => setVariantLabel(e.target.value)}
                                                        className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none w-40"
                                                    />

                                                    <select
                                                        value={variantType}
                                                        onChange={e => setVariantType(e.target.value)}
                                                        className="px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none"
                                                    >
                                                        <option value="2D">2D Plan</option>
                                                        <option value="3D">3D Plan</option>
                                                        <option value="Info">Info / Blueprint</option>
                                                        <option value="General">General</option>
                                                    </select>

                                                    <label className="px-4 py-1.5 bg-[#05144b] hover:bg-[#4c1d95] text-white rounded-xl text-xs font-bold cursor-pointer transition-all flex items-center gap-1.5 shadow-sm active:scale-95">
                                                        {uploading ? (
                                                            <i className="fa-solid fa-spinner animate-spin"></i>
                                                        ) : (
                                                            <i className="fa-solid fa-cloud-arrow-up"></i>
                                                        )}
                                                        Upload Plan
                                                        <input
                                                            type="file"
                                                            multiple
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={e => handleFileUpload(e, 'floor-variants')}
                                                            disabled={uploading}
                                                        />
                                                    </label>
                                                </div>
                                            </div>

                                            {/* LEVEL 1 UNIT CONFIGURATION TABS (EXACT MATCH TO REFERENCE SCREENSHOT) */}
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                                                        <i className="fa-solid fa-layer-group text-indigo-600 text-[10px]"></i>
                                                        Unit Configuration Tabs (Project Level 1)
                                                    </span>
                                                    {selectedUnitTypeTab !== 'All' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const currentPrice = activeFolder.floorVariants.find(fv => (fv.unitType || '2 BHK').toLowerCase() === selectedUnitTypeTab.toLowerCase() && fv.priceRange && fv.priceRange !== 'On Request')?.priceRange || 'On Request';
                                                                const newPrice = window.prompt(`Update price range for "${selectedUnitTypeTab}" (e.g. ₹65 Lakh - ₹85 Lakh or On Request):`, currentPrice);
                                                                if (newPrice !== null) {
                                                                    handleUpdateUnitTypePrice(selectedUnitTypeTab, newPrice.trim() || 'On Request');
                                                                }
                                                            }}
                                                            className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                                                        >
                                                            <i className="fa-solid fa-pen text-[9px]"></i>
                                                            Edit Price for {selectedUnitTypeTab}
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-3 overflow-x-auto no-scrollbar pb-2">
                                                    {/* ALL TAB */}
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedUnitTypeTab('All')}
                                                        className={`flex flex-col items-start px-5 py-2.5 sm:px-6 sm:py-3 rounded-2xl border-2 transition-all min-w-[125px] sm:min-w-[150px] text-left group shrink-0 ${
                                                            selectedUnitTypeTab === 'All'
                                                                ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] border-[#f1ae24] shadow-xl scale-[1.02]'
                                                                : 'border-slate-200 hover:border-slate-300 bg-white'
                                                        }`}
                                                    >
                                                        <span className={`text-[12px] md:text-[13px] font-black mb-1 ${
                                                            selectedUnitTypeTab === 'All' ? 'text-[#f1ae24]' : 'text-slate-400 group-hover:text-slate-600'
                                                        }`}>
                                                            ALL UNITS
                                                        </span>
                                                        <span className={`text-[13px] md:text-[15px] font-black leading-none ${
                                                            selectedUnitTypeTab === 'All' ? 'text-white' : 'text-slate-900'
                                                        }`}>
                                                            {activeFolder.floorVariants.length} Plans
                                                        </span>
                                                    </button>

                                                    {/* DYNAMIC UNIT TYPE TABS */}
                                                    {availableUnitTabs.map(uType => {
                                                        const count = activeFolder.floorVariants.filter(fv => (fv.unitType || '2 BHK').toLowerCase() === uType.toLowerCase()).length;
                                                        const price = activeFolder.floorVariants.find(fv => (fv.unitType || '2 BHK').toLowerCase() === uType.toLowerCase() && fv.priceRange && fv.priceRange !== 'On Request')?.priceRange || 'On Request';
                                                        const isActive = selectedUnitTypeTab.toLowerCase() === uType.toLowerCase();

                                                        return (
                                                            <button
                                                                key={uType}
                                                                type="button"
                                                                onClick={() => {
                                                                    setSelectedUnitTypeTab(uType);
                                                                    setUploadUnitType(uType || '2 BHK');
                                                                }}
                                                                className={`flex flex-col items-start px-5 py-2.5 sm:px-6 sm:py-3 rounded-2xl border-2 transition-all min-w-[130px] sm:min-w-[160px] text-left group shrink-0 ${
                                                                    isActive
                                                                        ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] border-[#f1ae24] shadow-xl scale-[1.02]'
                                                                        : 'border-slate-200 hover:border-slate-300 bg-white'
                                                                }`}
                                                            >
                                                                <div className="flex items-center justify-between w-full mb-1">
                                                                    <span className={`text-[12px] md:text-[13px] font-black ${
                                                                        isActive ? 'text-[#f1ae24]' : 'text-slate-400 group-hover:text-slate-600'
                                                                    }`}>
                                                                        {uType}
                                                                    </span>
                                                                    <span className={`text-[10px] font-black px-1.5 py-0.2 rounded-full ${
                                                                        isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                                                                    }`}>
                                                                        {count}
                                                                    </span>
                                                                </div>
                                                                <span className={`text-[13px] md:text-[15px] font-black leading-none ${
                                                                    isActive ? 'text-white' : 'text-slate-900'
                                                                }`}>
                                                                    {price}
                                                                </span>
                                                            </button>
                                                        );
                                                    })}

                                                    {/* ADD UNIT TYPE TAB BUTTON */}
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsAddUnitTypeModalOpen(true)}
                                                        className="px-4 py-3 rounded-2xl border-2 border-dashed border-indigo-300 hover:border-indigo-500 bg-indigo-50/50 hover:bg-indigo-50 text-indigo-700 font-bold text-xs flex items-center gap-2 shrink-0 transition-all active:scale-95"
                                                        title="Add Unit Tab (e.g. 1 BHK, 4 BHK, Villa, Penthouse)"
                                                    >
                                                        <i className="fa-solid fa-plus text-xs"></i>
                                                        <span>Add Unit Tab</span>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* FLOOR PLANS GRID */}
                                            {filteredVariants.length === 0 ? (
                                                <div className="text-center py-14 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                                                    <i className="fa-solid fa-layer-group text-3xl text-slate-300 mb-2"></i>
                                                    <p className="text-xs font-bold text-slate-600">
                                                        No floor plans uploaded for {selectedUnitTypeTab === 'All' ? 'this folder' : selectedUnitTypeTab}
                                                    </p>
                                                    <p className="text-[11px] text-slate-400 mb-3">
                                                        Select files and click "Upload Plan" above to add 2D or 3D layouts directly into this tab.
                                                    </p>
                                                    <label className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#05144b] text-white text-xs font-bold rounded-xl cursor-pointer hover:bg-indigo-900 transition-all shadow-sm">
                                                        <i className="fa-solid fa-cloud-arrow-up text-xs"></i>
                                                        Upload into {selectedUnitTypeTab}
                                                        <input
                                                            type="file"
                                                            multiple
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={e => handleFileUpload(e, 'floor-variants')}
                                                            disabled={uploading}
                                                        />
                                                    </label>
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {/* SELECTION TOOLBAR */}
                                                    <div className="flex items-center justify-between gap-3 bg-slate-50 px-3.5 py-2 rounded-xl border border-slate-200">
                                                        <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-slate-700 hover:text-indigo-600">
                                                            <input
                                                                type="checkbox"
                                                                checked={areAllFilesSelected(filteredVariants.map(fv => ({ filePath: fv.url })))}
                                                                onChange={() => handleToggleSelectAllFiles(filteredVariants.map(fv => ({
                                                                    subFolder: 'floor-variants',
                                                                    filePath: fv.url,
                                                                    fileId: fv._id
                                                                })))}
                                                                className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 cursor-pointer accent-rose-600"
                                                            />
                                                            <span>Select All in {selectedUnitTypeTab} ({filteredVariants.length})</span>
                                                        </label>

                                                        {selectedFiles.some(f => f.subFolder === 'floor-variants') && (
                                                            <button
                                                                type="button"
                                                                onClick={handlePromptBulkDeleteFiles}
                                                                className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm transition active:scale-95"
                                                            >
                                                                <i className="fa-solid fa-trash-can text-[11px]"></i>
                                                                <span>Delete Selected ({selectedFiles.filter(f => f.subFolder === 'floor-variants').length})</span>
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                                        {filteredVariants.map((fv, idx) => (
                                                            <div
                                                                key={idx}
                                                                className={`group bg-white rounded-xl border ${
                                                                    isFileSelected(fv.url)
                                                                        ? 'ring-2 ring-rose-500 border-rose-400 shadow-md'
                                                                        : 'border-slate-200 hover:shadow-md'
                                                                } overflow-hidden shadow-sm transition-all relative`}
                                                            >
                                                                <div 
                                                                    onClick={() => setPreviewUrl(getImageUrl(fv.url))}
                                                                    className="h-36 bg-slate-100 overflow-hidden cursor-pointer relative"
                                                                >
                                                                    <img src={getImageUrl(fv.url)} alt={fv.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                                                    
                                                                    {/* CHECKBOX */}
                                                                    <div
                                                                        onClick={(e) => handleToggleSelectFile({ subFolder: 'floor-variants', filePath: fv.url, fileId: fv._id }, e)}
                                                                        className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-lg backdrop-blur-md flex items-center justify-center transition-all cursor-pointer border ${
                                                                            isFileSelected(fv.url)
                                                                                ? 'bg-rose-600 border-white text-white shadow-md'
                                                                                : 'bg-black/50 hover:bg-black/70 border-white/40 text-white'
                                                                        }`}
                                                                        title={isFileSelected(fv.url) ? "Deselect" : "Select for deletion"}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isFileSelected(fv.url)}
                                                                            onChange={() => {}}
                                                                            className="w-3.5 h-3.5 rounded text-rose-600 focus:ring-rose-500 cursor-pointer accent-rose-600 pointer-events-none"
                                                                        />
                                                                    </div>

                                                                    {/* BADGES */}
                                                                    <div className="absolute bottom-2 left-2 flex items-center gap-1 z-10 pointer-events-none">
                                                                        <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-md bg-[#05144b]/90 backdrop-blur-xs text-white shadow-xs">
                                                                            {fv.unitType || '2 BHK'}
                                                                        </span>
                                                                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md shadow-xs ${
                                                                            fv.variantType === '3D' ? 'bg-amber-400 text-slate-950 font-black' :
                                                                            fv.variantType === 'Info' ? 'bg-purple-600 text-white' :
                                                                            'bg-white/95 text-slate-800'
                                                                        }`}>
                                                                            {fv.variantType || '2D'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="p-3">
                                                                    <div className="flex items-center justify-between mb-1">
                                                                        <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded truncate max-w-[110px]">
                                                                            {fv.subUnitVariant || 'Standard'}
                                                                        </span>
                                                                        <span className="text-[10px] font-bold text-slate-500">
                                                                            {fv.priceRange || 'On Request'}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-xs font-bold text-slate-800 truncate" title={fv.title || fv.filename}>
                                                                        {fv.title || fv.filename || `Variant #${idx+1}`}
                                                                    </p>
                                                                </div>
                                                                <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); handleSetCoverImage(fv.url); }}
                                                                        className="w-7 h-7 rounded-full bg-slate-900/80 hover:bg-amber-500 text-white flex items-center justify-center shadow transition-colors"
                                                                        title="Set as Project Cover"
                                                                    >
                                                                        <i className="fa-solid fa-star text-[10px]"></i>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setEditingVariant({
                                                                                itemId: fv._id,
                                                                                url: fv.url,
                                                                                title: fv.title || '',
                                                                                unitType: fv.unitType || '2 BHK',
                                                                                priceRange: fv.priceRange || 'On Request',
                                                                                variantType: (fv.variantType as any) || '2D',
                                                                                subUnitVariant: fv.subUnitVariant || ''
                                                                            });
                                                                        }}
                                                                        className="w-7 h-7 rounded-full bg-slate-900/80 hover:bg-indigo-600 text-white flex items-center justify-center shadow transition-colors"
                                                                        title="Edit Floor Variant"
                                                                    >
                                                                        <i className="fa-solid fa-pen text-[10px]"></i>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); handleDeleteFile('floor-variants', fv.url, fv._id); }}
                                                                        className="w-7 h-7 rounded-full bg-rose-600 text-white flex items-center justify-center shadow transition-colors"
                                                                        title="Delete"
                                                                    >
                                                                        <i className="fa-solid fa-trash text-xs"></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* TAB 2: GALLERY */}
                                {activeTab === 'gallery' && (
                                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-100">
                                            {/* ALBUM SELECTOR */}
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    { key: 'exterior', label: 'Exterior', icon: 'fa-building' },
                                                    { key: 'interior', label: 'Interior', icon: 'fa-couch' },
                                                    { key: 'amenities', label: 'Amenities', icon: 'fa-gem' },
                                                    { key: 'construction', label: 'Progress', icon: 'fa-helmet-safety' },
                                                    { key: 'topPhotos', label: 'Top Photos', icon: 'fa-star' },
                                                    { key: 'other', label: 'Other', icon: 'fa-images' },
                                                ].map(alb => (
                                                    <button
                                                        key={alb.key}
                                                        onClick={() => setGalleryAlbum(alb.key as any)}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${
                                                            galleryAlbum === alb.key
                                                                ? 'bg-[#05144b] text-white shadow'
                                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                        }`}
                                                    >
                                                        <i className={`fa-solid ${alb.icon}`}></i>
                                                        {alb.label} ({activeFolder.gallery?.[alb.key as keyof typeof activeFolder.gallery]?.length || 0})
                                                    </button>
                                                ))}
                                            </div>

                                            <label className="px-4 py-2 bg-[#05144b] text-white rounded-xl text-xs font-bold cursor-pointer hover:bg-[#4c1d95] transition-all flex items-center gap-2 self-start md:self-auto">
                                                {uploading ? (
                                                    <i className="fa-solid fa-spinner animate-spin"></i>
                                                ) : (
                                                    <i className="fa-solid fa-cloud-arrow-up"></i>
                                                )}
                                                Upload to {galleryAlbum}
                                                <input
                                                    type="file"
                                                    multiple
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={e => handleFileUpload(e, `gallery/${galleryAlbum}`)}
                                                    disabled={uploading}
                                                />
                                            </label>
                                        </div>

                                        {(!activeFolder.gallery?.[galleryAlbum] || activeFolder.gallery[galleryAlbum].length === 0) ? (
                                            <div className="text-center py-16 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                                                <i className="fa-solid fa-images text-3xl text-slate-300 mb-2"></i>
                                                <p className="text-xs font-bold text-slate-600">No images in {galleryAlbum}</p>
                                                <p className="text-[11px] text-slate-400">Click upload above to add photos to this album</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {/* GALLERY SELECTION TOOLBAR */}
                                                <div className="flex items-center justify-between gap-3 bg-slate-50 px-3.5 py-2 rounded-xl border border-slate-200">
                                                    <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-slate-700 hover:text-indigo-600">
                                                        <input
                                                            type="checkbox"
                                                            checked={areAllFilesSelected(activeFolder.gallery[galleryAlbum].map(url => ({ filePath: url })))}
                                                            onChange={() => handleToggleSelectAllFiles(activeFolder.gallery[galleryAlbum].map(url => ({
                                                                subFolder: `gallery/${galleryAlbum}`,
                                                                filePath: url
                                                            })))}
                                                            className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 cursor-pointer accent-rose-600"
                                                        />
                                                        <span>Select All in {galleryAlbum} ({activeFolder.gallery[galleryAlbum].length})</span>
                                                    </label>

                                                    {selectedFiles.some(f => f.subFolder === `gallery/${galleryAlbum}`) && (
                                                        <button
                                                            type="button"
                                                            onClick={handlePromptBulkDeleteFiles}
                                                            className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm transition active:scale-95"
                                                        >
                                                            <i className="fa-solid fa-trash-can text-[11px]"></i>
                                                            <span>Delete Selected ({selectedFiles.filter(f => f.subFolder === `gallery/${galleryAlbum}`).length})</span>
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                                    {activeFolder.gallery[galleryAlbum].map((url, idx) => (
                                                        <div
                                                            key={idx}
                                                            className={`group bg-slate-100 rounded-xl overflow-hidden aspect-video relative shadow-sm border ${
                                                                isFileSelected(url)
                                                                    ? 'ring-2 ring-rose-500 border-rose-400 shadow-md'
                                                                    : 'border-slate-200'
                                                            }`}
                                                        >
                                                            <img
                                                                src={getImageUrl(url)}
                                                                alt=""
                                                                className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform"
                                                                onClick={() => setPreviewUrl(getImageUrl(url))}
                                                            />

                                                            {/* CHECKBOX */}
                                                            <div
                                                                onClick={(e) => handleToggleSelectFile({ subFolder: `gallery/${galleryAlbum}`, filePath: url }, e)}
                                                                className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-lg backdrop-blur-md flex items-center justify-center transition-all cursor-pointer border ${
                                                                    isFileSelected(url)
                                                                        ? 'bg-rose-600 border-white text-white shadow-md'
                                                                        : 'bg-black/50 hover:bg-black/70 border-white/40 text-white'
                                                                }`}
                                                                title={isFileSelected(url) ? "Deselect" : "Select for deletion"}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isFileSelected(url)}
                                                                    onChange={() => {}}
                                                                    className="w-3.5 h-3.5 rounded text-rose-600 focus:ring-rose-500 cursor-pointer accent-rose-600 pointer-events-none"
                                                                />
                                                            </div>

                                                            <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => { e.stopPropagation(); handleSetCoverImage(url); }}
                                                                    className="w-7 h-7 rounded-full bg-slate-900/80 hover:bg-amber-500 text-white flex items-center justify-center shadow transition-colors"
                                                                    title="Set as Project Cover"
                                                                >
                                                                    <i className="fa-solid fa-star text-[10px]"></i>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => { e.stopPropagation(); handleDeleteFile(`gallery/${galleryAlbum}`, url); }}
                                                                    className="w-7 h-7 rounded-full bg-rose-600 text-white flex items-center justify-center shadow transition-colors"
                                                                    title="Delete"
                                                                >
                                                                    <i className="fa-solid fa-trash text-xs"></i>
                                                                </button>
                                                            </div>
                                                            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between text-[10px] text-white z-10">
                                                                <span className="font-bold text-[9px] text-slate-300">Move:</span>
                                                                <select
                                                                    value=""
                                                                    onChange={(e) => {
                                                                        if (e.target.value) handleMoveGalleryAlbum(url, galleryAlbum, e.target.value);
                                                                    }}
                                                                    className="bg-black/70 hover:bg-black text-white text-[9px] font-bold px-1 py-0.5 rounded border border-white/30 focus:outline-none cursor-pointer"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <option value="" disabled>Select album</option>
                                                                    {[
                                                                        { key: 'exterior', label: 'Exterior' },
                                                                        { key: 'interior', label: 'Interior' },
                                                                        { key: 'amenities', label: 'Amenities' },
                                                                        { key: 'construction', label: 'Progress' },
                                                                        { key: 'topPhotos', label: 'Top Photos' },
                                                                        { key: 'other', label: 'Other' },
                                                                    ].filter(a => a.key !== galleryAlbum).map(a => (
                                                                        <option key={a.key} value={a.key} className="bg-slate-900 text-white">{a.label}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* TAB 3: DOCUMENTS */}
                                {activeTab === 'documents' && (
                                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-slate-100">
                                            {/* BROCHURE PDF */}
                                            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="font-bold text-xs text-slate-800 flex items-center gap-2">
                                                        <i className="fa-solid fa-file-pdf text-rose-500 text-base"></i>
                                                        Project Brochure (PDF)
                                                    </h4>
                                                    {activeFolder.documents?.brochure && (
                                                        <button
                                                            onClick={() => handleDeleteFile('documents', activeFolder.documents!.brochure!)}
                                                            className="text-rose-600 text-xs font-bold hover:underline"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>

                                                {activeFolder.documents?.brochure ? (
                                                    <div className="p-3 bg-white rounded-lg border border-slate-200 flex items-center justify-between">
                                                        <span className="text-xs font-bold text-slate-700 truncate">
                                                            {activeFolder.documents.brochure.split('/').pop()}
                                                        </span>
                                                        <a
                                                            href={getImageUrl(activeFolder.documents.brochure)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-indigo-600 text-xs font-bold flex items-center gap-1 hover:underline shrink-0"
                                                        >
                                                            <i className="fa-solid fa-eye"></i> View PDF
                                                        </a>
                                                    </div>
                                                ) : (
                                                    <label className="p-4 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 transition-colors">
                                                        <i className="fa-solid fa-upload text-slate-400 mb-1"></i>
                                                        <span className="text-xs font-bold text-slate-600">Upload Brochure PDF</span>
                                                        <input
                                                            type="file"
                                                            accept="application/pdf"
                                                            className="hidden"
                                                            onChange={e => handleFileUpload(e, 'documents/brochure')}
                                                        />
                                                    </label>
                                                )}
                                            </div>

                                            {/* TP / MASTER PLAN IMAGE */}
                                            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="font-bold text-xs text-slate-800 flex items-center gap-2">
                                                        <i className="fa-solid fa-map-location-dot text-indigo-500 text-base"></i>
                                                        Town Planning / Master Plan Image
                                                    </h4>
                                                    {activeFolder.documents?.tpImage && (
                                                        <button
                                                            onClick={() => handleDeleteFile('documents', activeFolder.documents!.tpImage!)}
                                                            className="text-rose-600 text-xs font-bold hover:underline"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>

                                                {activeFolder.documents?.tpImage ? (
                                                    <div className="relative rounded-lg overflow-hidden h-28 bg-white border border-slate-200">
                                                        <img
                                                            src={getImageUrl(activeFolder.documents.tpImage)}
                                                            alt="Town Planning Master Plan"
                                                            className="w-full h-full object-cover cursor-pointer"
                                                            onClick={() => setPreviewUrl(getImageUrl(activeFolder.documents!.tpImage!))}
                                                        />
                                                    </div>
                                                ) : (
                                                    <label className="p-4 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 transition-colors">
                                                        <i className="fa-solid fa-upload text-slate-400 mb-1"></i>
                                                        <span className="text-xs font-bold text-slate-600">Upload TP / Master Plan Image</span>
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={e => handleFileUpload(e, 'documents/tp')}
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                        </div>

                                        {/* LEGAL DOCUMENTS */}
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h4 className="font-bold text-xs text-slate-800">Legal Documents & Approvals</h4>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        placeholder="Document Title e.g. RERA Certificate"
                                                        value={docTitle}
                                                        onChange={e => setDocTitle(e.target.value)}
                                                        className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold focus:outline-none"
                                                    />
                                                    <label className="px-3 py-1.5 bg-[#05144b] text-white rounded-lg text-xs font-bold cursor-pointer hover:bg-[#4c1d95] transition-all flex items-center gap-1.5">
                                                        <i className="fa-solid fa-plus"></i> Add Legal Doc
                                                        <input
                                                            type="file"
                                                            accept="image/*,application/pdf"
                                                            className="hidden"
                                                            onChange={e => handleFileUpload(e, 'documents/legal')}
                                                        />
                                                    </label>
                                                </div>
                                            </div>

                                            {(!activeFolder.documents?.legalDocuments || activeFolder.documents.legalDocuments.length === 0) ? (
                                                <div className="text-center py-10 bg-slate-50/50 rounded-xl border border-dashed border-slate-200 text-xs text-slate-500">
                                                    No legal documents added yet.
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {/* LEGAL DOCUMENTS SELECTION TOOLBAR */}
                                                    <div className="flex items-center justify-between gap-3 bg-slate-50 px-3.5 py-2 rounded-xl border border-slate-200">
                                                        <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-bold text-slate-700 hover:text-indigo-600">
                                                            <input
                                                                type="checkbox"
                                                                checked={areAllFilesSelected(activeFolder.documents.legalDocuments.map(d => ({ filePath: d.url })))}
                                                                onChange={() => handleToggleSelectAllFiles(activeFolder.documents.legalDocuments.map(d => ({
                                                                    subFolder: 'documents',
                                                                    filePath: d.url,
                                                                    fileId: d._id
                                                                })))}
                                                                className="w-4 h-4 rounded text-rose-600 focus:ring-rose-500 cursor-pointer accent-rose-600"
                                                            />
                                                            <span>Select All ({activeFolder.documents.legalDocuments.length})</span>
                                                        </label>

                                                        {selectedFiles.some(f => f.subFolder === 'documents') && (
                                                            <button
                                                                type="button"
                                                                onClick={handlePromptBulkDeleteFiles}
                                                                className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm transition active:scale-95"
                                                            >
                                                                <i className="fa-solid fa-trash-can text-[11px]"></i>
                                                                <span>Delete Selected ({selectedFiles.filter(f => f.subFolder === 'documents').length})</span>
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                                        {activeFolder.documents.legalDocuments.map((doc, idx) => (
                                                            <div
                                                                key={idx}
                                                                className={`p-3 bg-slate-50 rounded-xl border ${
                                                                    isFileSelected(doc.url)
                                                                        ? 'ring-2 ring-rose-500 border-rose-400 bg-rose-50/20 shadow-sm'
                                                                        : 'border-slate-200'
                                                                } flex items-center justify-between gap-2 transition`}
                                                            >
                                                                <div className="flex items-center gap-2.5 truncate">
                                                                    {/* CHECKBOX */}
                                                                    <div
                                                                        onClick={(e) => handleToggleSelectFile({ subFolder: 'documents', filePath: doc.url, fileId: doc._id }, e)}
                                                                        className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all cursor-pointer border shrink-0 ${
                                                                            isFileSelected(doc.url)
                                                                                ? 'bg-rose-600 border-rose-600 text-white shadow-sm'
                                                                                : 'bg-white hover:bg-slate-100 border-slate-300 text-slate-600'
                                                                        }`}
                                                                        title={isFileSelected(doc.url) ? "Deselect" : "Select for deletion"}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isFileSelected(doc.url)}
                                                                            onChange={() => {}}
                                                                            className="w-3.5 h-3.5 rounded text-rose-600 focus:ring-rose-500 cursor-pointer accent-rose-600 pointer-events-none"
                                                                        />
                                                                    </div>

                                                                    <i className="fa-solid fa-file-shield text-indigo-600 text-lg shrink-0"></i>
                                                                    <div className="truncate">
                                                                        <div className="text-xs font-bold text-slate-800 truncate">{doc.title || doc.filename}</div>
                                                                        <a href={getImageUrl(doc.url)} target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-600 hover:underline">
                                                                            Preview
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-1 shrink-0">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setEditingDoc({ itemId: doc._id, url: doc.url, title: doc.title || '' })}
                                                                        className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 hover:bg-indigo-600 hover:text-white flex items-center justify-center transition-all"
                                                                        title="Edit Document Title"
                                                                    >
                                                                        <i className="fa-solid fa-pen text-[10px]"></i>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteFile('documents', doc.url, doc._id)}
                                                                        className="w-7 h-7 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white flex items-center justify-center transition-all"
                                                                        title="Delete"
                                                                    >
                                                                        <i className="fa-solid fa-trash text-xs"></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* TAB 4: COVER IMAGE */}
                                {activeTab === 'cover' && (
                                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
                                        <div>
                                            <h3 className="text-base font-bold text-slate-900">Main Cover / Featured Photo</h3>
                                            <p className="text-xs text-slate-500">This photo will automatically become the project's cover image</p>
                                        </div>

                                        <div className="max-w-xl">
                                            {activeFolder.coverImage ? (
                                                <div className="relative rounded-2xl overflow-hidden aspect-video border border-slate-200 shadow-md">
                                                    <img
                                                        src={getImageUrl(activeFolder.coverImage)}
                                                        alt="Cover"
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <div className="absolute top-3 right-3 flex gap-2">
                                                        <button
                                                            onClick={() => handleDeleteFile('cover', activeFolder.coverImage!)}
                                                            className="w-8 h-8 rounded-full bg-rose-600 text-white flex items-center justify-center shadow"
                                                            title="Delete"
                                                        >
                                                            <i className="fa-solid fa-trash text-xs"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <label className="w-full aspect-video rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 transition-colors">
                                                    <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-2 text-indigo-600 text-2xl">
                                                        <i className="fa-solid fa-camera"></i>
                                                    </div>
                                                    <span className="text-xs font-bold text-slate-700">Upload Project Cover Photo</span>
                                                    <span className="text-[10px] text-slate-400 mt-1">Recommended: 1920x1080 (JPG, PNG, WEBP)</span>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={e => handleFileUpload(e, 'cover')}
                                                    />
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* CREATE FOLDER MODAL */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in-95">
                        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-[#05144b] text-white flex items-center justify-center font-bold text-xs">
                                    <i className="fa-solid fa-folder-plus"></i>
                                </div>
                                <h3 className="font-bold text-slate-900 text-base">Create Media Folder</h3>
                            </div>
                            <button
                                onClick={() => setIsCreateModalOpen(false)}
                                className="w-8 h-8 rounded-full bg-slate-100 text-slate-400 hover:text-slate-600 flex items-center justify-center"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <form onSubmit={handleCreateFolder} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">
                                    Folder Number <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    required
                                    min="1"
                                    value={newFolderNumber}
                                    onChange={e => setNewFolderNumber(parseInt(e.target.value) || '')}
                                    placeholder="e.g. 1"
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-[#05144b]/20"
                                />
                                <p className="text-[10px] text-slate-400 mt-1">This number will be entered in Add Project or Excel to load images.</p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">
                                    Folder / Project Name (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={newFolderName}
                                    onChange={e => setNewFolderName(e.target.value)}
                                    placeholder="e.g. Godrej Altus / Project 1"
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#05144b]/20"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">
                                    Description (Optional)
                                </label>
                                <textarea
                                    value={newFolderDesc}
                                    onChange={e => setNewFolderDesc(e.target.value)}
                                    rows={2}
                                    placeholder="Add any notes about this project's media..."
                                    className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#05144b]/20"
                                />
                            </div>

                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={creating}
                                    className="flex-1 py-2.5 rounded-xl bg-[#05144b] hover:bg-[#4c1d95] text-white font-bold text-xs flex items-center justify-center gap-2 shadow"
                                >
                                    {creating ? <i className="fa-solid fa-spinner animate-spin"></i> : <i className="fa-solid fa-check"></i>}
                                    Create Folder
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* UPLOAD ZIP MODAL */}
            {isZipModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in-95">
                        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 text-white flex items-center justify-center font-bold text-sm shadow-md">
                                    <i className="fa-solid fa-file-zipper"></i>
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900 text-base">Upload Media Folder ZIP</h3>
                                    <p className="text-[11px] text-slate-400">Extracts and auto-organizes your project media files</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsZipModalOpen(false)}
                                className="w-8 h-8 rounded-full bg-slate-100 text-slate-400 hover:text-slate-600 flex items-center justify-center"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <form onSubmit={handleZipUploadSubmit} className="space-y-4">
                            {/* MODE TOGGLE */}
                            <div className="flex p-1 bg-slate-100 rounded-xl gap-1">
                                <button
                                    type="button"
                                    onClick={() => { setZipTargetMode('new'); setZipFiles([]); }}
                                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${zipTargetMode === 'new' ? 'bg-white text-indigo-950 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                                >
                                    <i className="fa-solid fa-folder-plus mr-1"></i> Single Folder
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setZipTargetMode('bulk'); setZipFile(null); }}
                                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${zipTargetMode === 'bulk' ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                                >
                                    <i className="fa-solid fa-boxes-stacked mr-1"></i> Bulk Multiple
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setZipTargetMode('existing'); setZipFiles([]); }}
                                    className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${zipTargetMode === 'existing' ? 'bg-white text-indigo-950 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                                >
                                    <i className="fa-solid fa-folder-open mr-1"></i> Into Existing
                                </button>
                            </div>

                            {zipTargetMode === 'new' && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Folder Number <span className="text-rose-500">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            required
                                            min="1"
                                            value={zipNewFolderNumber}
                                            onChange={e => setZipNewFolderNumber(parseInt(e.target.value) || '')}
                                            placeholder="e.g. 1"
                                            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Folder / Project Name
                                        </label>
                                        <input
                                            type="text"
                                            value={zipNewFolderName}
                                            onChange={e => setZipNewFolderName(e.target.value)}
                                            placeholder="e.g. Godrej Horizon"
                                            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Description / Remarks (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={zipNewFolderDesc}
                                            onChange={e => setZipNewFolderDesc(e.target.value)}
                                            placeholder="e.g. Master folder with variants & gallery"
                                            className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                        />
                                    </div>
                                </div>
                            )}

                            {zipTargetMode === 'bulk' && (
                                <div className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200/70 text-xs space-y-1.5">
                                    <div className="font-extrabold text-indigo-950 flex items-center gap-1.5">
                                        <i className="fa-solid fa-wand-magic-sparkles text-indigo-600"></i>
                                        <span>How Multiple Folder Upload Works:</span>
                                    </div>
                                    <ul className="text-slate-600 text-[11px] space-y-1 list-disc list-inside">
                                        <li><b>Option 1:</b> Select <b>multiple .ZIP files</b> together (e.g. <code>Godrej.zip</code>, <code>Prestige.zip</code>). Each will become a separate numbered folder.</li>
                                        <li><b>Option 2:</b> Upload <b>1 Master .ZIP file</b> that contains multiple folders inside (e.g. <code>1/</code>, <code>2/</code>, <code>3/</code> or <code>ProjectA/</code>, <code>ProjectB/</code>).</li>
                                    </ul>
                                </div>
                            )}

                            {zipTargetMode === 'existing' && (
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">
                                        Target Folder <span className="text-rose-500">*</span>
                                    </label>
                                    <select
                                        required
                                        value={zipSelectedFolder}
                                        onChange={e => setZipSelectedFolder(parseInt(e.target.value) || '')}
                                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                    >
                                        {folders.length === 0 ? (
                                            <option value="">No folders available</option>
                                        ) : (
                                            folders.map(f => (
                                                <option key={f._id} value={f.folderNumber}>
                                                    Folder #{f.folderNumber} - {f.folderName} ({f.totalFiles} files)
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                            )}

                            {/* ZIP FILE INPUT DROPZONE */}
                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">
                                    {zipTargetMode === 'bulk' ? 'Select One or Multiple .ZIP Files' : 'Select .ZIP File'} <span className="text-rose-500">*</span>
                                </label>
                                <div className="border-2 border-dashed border-indigo-200 hover:border-indigo-500 rounded-2xl p-6 text-center bg-indigo-50/20 hover:bg-indigo-50/40 transition-all cursor-pointer relative">
                                    <input
                                        type="file"
                                        accept=".zip,application/zip,application/x-zip-compressed"
                                        multiple={zipTargetMode === 'bulk'}
                                        required
                                        onChange={e => {
                                            if (e.target.files) {
                                                const fileList = Array.from(e.target.files);
                                                if (zipTargetMode === 'bulk') {
                                                    setZipFiles(fileList);
                                                    setZipFile(fileList[0] || null);
                                                } else {
                                                    setZipFile(fileList[0] || null);
                                                    setZipFiles([]);
                                                }
                                            }
                                        }}
                                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                    />
                                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-xl mx-auto mb-2">
                                        <i className="fa-solid fa-cloud-arrow-up"></i>
                                    </div>
                                    {zipTargetMode === 'bulk' && zipFiles.length > 0 ? (
                                        <div>
                                            <p className="text-xs font-black text-indigo-950">
                                                {zipFiles.length} ZIP file(s) selected
                                            </p>
                                            <p className="text-[10px] text-slate-500 mt-0.5">
                                                Total: {(zipFiles.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024)).toFixed(2)} MB
                                            </p>
                                            <div className="mt-2 flex flex-wrap gap-1 justify-center max-h-24 overflow-y-auto">
                                                {zipFiles.map((f, i) => (
                                                    <span key={i} className="text-[10px] font-bold bg-white px-2 py-0.5 rounded-md border border-slate-200 text-slate-700 shadow-2xs">
                                                        {f.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ) : zipFile ? (
                                        <div>
                                            <p className="text-xs font-black text-indigo-950 truncate max-w-xs mx-auto">{zipFile.name}</p>
                                            <p className="text-[10px] text-slate-400 mt-0.5">{(zipFile.size / (1024 * 1024)).toFixed(2)} MB • Click or drag to replace</p>
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="text-xs font-bold text-slate-700">
                                                {zipTargetMode === 'bulk'
                                                    ? 'Click or drag & drop multiple .ZIP files here'
                                                    : 'Click or drag & drop a .ZIP file here'}
                                            </p>
                                            <p className="text-[10px] text-slate-400 mt-0.5">
                                                {zipTargetMode === 'bulk'
                                                    ? 'Upload multiple project ZIPs or 1 master multi-folder ZIP'
                                                    : 'ZIP with cover, floor-variants, gallery, documents'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* SAMPLE ZIP HELPER LINK */}
                            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-200/60 text-xs">
                                <div className="flex items-center gap-2 text-emerald-900 font-semibold text-[11px]">
                                    <i className="fa-solid fa-circle-question text-emerald-600"></i>
                                    <span>Need sample folder structure?</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleDownloadSampleZip}
                                    className="px-2.5 py-1 bg-white hover:bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-lg font-bold text-[10px] flex items-center gap-1 shadow-xs transition"
                                >
                                    <i className="fa-solid fa-download"></i> Sample ZIP
                                </button>
                            </div>

                            {/* SUBMIT BUTTONS */}
                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => { setIsZipModalOpen(false); setZipFiles([]); setZipFile(null); }}
                                    className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={zipUploading || (zipTargetMode === 'bulk' ? zipFiles.length === 0 && !zipFile : !zipFile)}
                                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold text-xs flex items-center justify-center gap-2 shadow disabled:opacity-50"
                                >
                                    {zipUploading ? (
                                        <>
                                            <i className="fa-solid fa-spinner animate-spin"></i>
                                            <span>Processing Folders...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up"></i>
                                            <span>{zipTargetMode === 'bulk' ? 'Upload & Create All Folders' : 'Upload & Extract'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* EDIT FOLDER MODAL */}
            {isEditModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                            <div className="flex items-center gap-2.5">
                                <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-black">
                                    <i className="fa-solid fa-pen"></i>
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-slate-900">Edit Media Folder</h3>
                                    <p className="text-[11px] text-slate-500">Folder #{editFolderNumber}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center text-sm transition"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSaveEditFolder} className="mt-4 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">
                                    Folder Number
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    required
                                    value={newAssignedNumber}
                                    onChange={e => setNewAssignedNumber(e.target.value === '' ? '' : Number(e.target.value))}
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                />
                                <p className="text-[10px] text-amber-600 mt-1">
                                    Changing folder number renames the storage folder on disk and updates all file paths automatically.
                                </p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">
                                    Folder / Project Name
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. Godrej Prime Heights"
                                    value={editFolderName}
                                    onChange={e => setEditFolderName(e.target.value)}
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">
                                    Description / Remarks (Optional)
                                </label>
                                <textarea
                                    rows={3}
                                    placeholder="Short notes about this media folder..."
                                    value={editFolderDesc}
                                    onChange={e => setEditFolderDesc(e.target.value)}
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                />
                            </div>

                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={savingFolder}
                                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-xs flex items-center justify-center gap-2 shadow disabled:opacity-50"
                                >
                                    {savingFolder ? (
                                        <>
                                            <i className="fa-solid fa-spinner animate-spin"></i>
                                            <span>Saving...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-floppy-disk"></i>
                                            <span>Save Changes</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* EDIT FLOOR VARIANT MODAL */}
            {editingVariant && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                            <div className="flex items-center gap-2.5">
                                <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center text-sm font-black">
                                    <i className="fa-solid fa-layer-group"></i>
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-slate-900">Edit Floor Variant</h3>
                                    <p className="text-[11px] text-slate-500">Update configuration & title</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setEditingVariant(null)}
                                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center text-sm transition"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSaveVariant} className="mt-4 space-y-4">
                            <div className="h-32 rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                                <img src={getImageUrl(editingVariant.url)} alt="" className="w-full h-full object-cover" />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">
                                    Variant Title / Label
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. 2 BHK Premium Layout"
                                    value={editingVariant.title}
                                    onChange={e => setEditingVariant({ ...editingVariant, title: e.target.value })}
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">
                                        Unit Type (Tab)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g. 2 BHK, 3 BHK, Penthouse"
                                        value={editingVariant.unitType || '2 BHK'}
                                        onChange={e => setEditingVariant({ ...editingVariant, unitType: e.target.value })}
                                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">
                                        Price Range
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g. On Request or ₹65 L - ₹85 L"
                                        value={editingVariant.priceRange || 'On Request'}
                                        onChange={e => setEditingVariant({ ...editingVariant, priceRange: e.target.value })}
                                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">
                                        Plan Type
                                    </label>
                                    <select
                                        value={editingVariant.variantType}
                                        onChange={e => setEditingVariant({ ...editingVariant, variantType: e.target.value as any })}
                                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                    >
                                        <option value="2D">2D Plan</option>
                                        <option value="3D">3D Plan</option>
                                        <option value="Info">Info / Blueprint</option>
                                        <option value="General">General</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1">
                                        Sub-unit Variant
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Type A / Standard / Luxury"
                                        value={editingVariant.subUnitVariant}
                                        onChange={e => setEditingVariant({ ...editingVariant, subUnitVariant: e.target.value })}
                                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setEditingVariant(null)}
                                    className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={savingVariant}
                                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-xs flex items-center justify-center gap-2 shadow disabled:opacity-50"
                                >
                                    {savingVariant ? (
                                        <>
                                            <i className="fa-solid fa-spinner animate-spin"></i>
                                            <span>Saving...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-check"></i>
                                            <span>Save Variant</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ADD UNIT TYPE MODAL */}
            {isAddUnitTypeModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                            <div className="flex items-center gap-2.5">
                                <div className="w-10 h-10 rounded-2xl bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] flex items-center justify-center text-base font-black shadow-md">
                                    <i className="fa-solid fa-layer-group"></i>
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-slate-900">Add Unit Type Tab</h3>
                                    <p className="text-[11px] text-slate-500">Create a new unit configuration tab</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsAddUnitTypeModalOpen(false)}
                                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center text-sm transition"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <form onSubmit={handleCreateUnitTypeTab} className="mt-4 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">
                                    Unit Type Name <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. 1 BHK, 4 BHK, Penthouse, Duplex, Villa"
                                    value={newUnitTypeName}
                                    onChange={e => setNewUnitTypeName(e.target.value)}
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {['1 BHK', '2.5 BHK', '3.5 BHK', '4 BHK', '5 BHK', 'Penthouse', 'Duplex', 'Villa'].map(quickType => (
                                        <button
                                            key={quickType}
                                            type="button"
                                            onClick={() => setNewUnitTypeName(quickType)}
                                            className="px-2 py-0.5 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg text-[10px] font-bold text-slate-600 transition"
                                        >
                                            + {quickType}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">
                                    Price Range (Display)
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. ₹85 Lakh - ₹1.2 Cr or On Request"
                                    value={newUnitTypePrice}
                                    onChange={e => setNewUnitTypePrice(e.target.value)}
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {['On Request', '₹45 Lakh - ₹65 Lakh', '₹75 Lakh - ₹1.1 Cr', '₹1.2 Cr - ₹1.8 Cr'].map(quickPrice => (
                                        <button
                                            key={quickPrice}
                                            type="button"
                                            onClick={() => setNewUnitTypePrice(quickPrice)}
                                            className="px-2 py-0.5 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg text-[10px] font-bold text-slate-600 transition"
                                        >
                                            {quickPrice}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="p-3 bg-indigo-50/60 rounded-xl border border-indigo-100 flex items-start gap-2.5">
                                <i className="fa-solid fa-circle-info text-indigo-600 text-xs mt-0.5"></i>
                                <p className="text-[11px] text-indigo-900 leading-relaxed font-medium">
                                    This tab will appear with the dark navy & gold design. Uploaded 2D/3D plans in this tab will be grouped under this unit configuration when assigned to projects.
                                </p>
                            </div>

                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsAddUnitTypeModalOpen(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white font-bold text-xs flex items-center justify-center gap-2 shadow hover:opacity-95"
                                >
                                    <i className="fa-solid fa-plus text-[#f1ae24]"></i>
                                    <span>Create Tab</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* EDIT LEGAL DOCUMENT MODAL */}
            {editingDoc && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                            <div className="flex items-center gap-2.5">
                                <div className="w-9 h-9 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">
                                    <i className="fa-solid fa-file-signature"></i>
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-slate-900">Edit Document</h3>
                                    <p className="text-[11px] text-slate-500">Update document title</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setEditingDoc(null)}
                                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center text-sm transition"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSaveDoc} className="mt-4 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">
                                    Document Title
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. RERA Registration Certificate"
                                    value={editingDoc.title}
                                    onChange={e => setEditingDoc({ ...editingDoc, title: e.target.value })}
                                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                />
                            </div>

                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setEditingDoc(null)}
                                    className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={savingDoc}
                                    className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-xs flex items-center justify-center gap-2 shadow disabled:opacity-50"
                                >
                                    {savingDoc ? (
                                        <>
                                            <i className="fa-solid fa-spinner animate-spin"></i>
                                            <span>Saving...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-check"></i>
                                            <span>Save Title</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* FLOATING ACTION BAR FOR SELECTED FILES */}
            {selectedFiles.length > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900/95 text-white backdrop-blur-md px-6 py-3.5 rounded-2xl shadow-2xl border border-white/20 flex items-center gap-4 animate-in slide-in-from-bottom-5 duration-200">
                    <div className="flex items-center gap-2.5">
                        <span className="w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center text-xs font-black shadow-inner">
                            {selectedFiles.length}
                        </span>
                        <span className="text-xs font-bold">
                            {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                        </span>
                    </div>
                    <div className="h-4 w-px bg-white/20"></div>
                    <button
                        type="button"
                        onClick={() => setSelectedFiles([])}
                        className="text-xs text-slate-400 hover:text-white transition font-bold"
                    >
                        Clear Selection
                    </button>
                    <button
                        type="button"
                        onClick={handlePromptBulkDeleteFiles}
                        className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg shadow-rose-600/30 transition active:scale-95"
                    >
                        <i className="fa-solid fa-trash-can text-xs"></i>
                        <span>Delete Selected ({selectedFiles.length})</span>
                    </button>
                </div>
            )}

            {/* CUSTOM DELETE CONFIRMATION DIALOG MODAL */}
            {deleteDialog && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 text-center animate-in fade-in zoom-in-95 duration-200">
                        <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center text-2xl mx-auto mb-4 border border-rose-100 shadow-sm">
                            <i className="fa-solid fa-triangle-exclamation"></i>
                        </div>

                        <h3 className="text-lg font-black text-slate-900 mb-2">
                            {deleteDialog.title}
                        </h3>

                        <p className="text-xs font-semibold text-slate-700 mb-2">
                            {deleteDialog.message}
                        </p>

                        {deleteDialog.subMessage && (
                            <p className="text-[11px] text-slate-500 mb-6 bg-slate-50 p-3 rounded-xl border border-slate-100 leading-relaxed text-left max-h-40 overflow-y-auto">
                                {deleteDialog.subMessage}
                            </p>
                        )}

                        <div className="flex items-center gap-2.5">
                            <button
                                type="button"
                                disabled={deleting}
                                onClick={() => setDeleteDialog(null)}
                                className="flex-1 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-xs transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={deleting}
                                onClick={handleConfirmDelete}
                                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-md shadow-rose-600/20 transition disabled:opacity-50"
                            >
                                {deleting ? (
                                    <>
                                        <i className="fa-solid fa-spinner animate-spin"></i>
                                        <span>Deleting...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-trash-can"></i>
                                        <span>Yes, Delete</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* LIGHTBOX PREVIEW */}
            {previewUrl && (
                <div 
                    onClick={() => setPreviewUrl(null)}
                    className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 cursor-pointer"
                >
                    <div className="relative max-w-4xl max-h-[90vh]">
                        <img src={previewUrl} alt="Preview" className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain mx-auto" />
                        <button
                            onClick={() => setPreviewUrl(null)}
                            className="absolute -top-10 right-0 text-white text-xl hover:text-amber-400"
                        >
                            <i className="fa-solid fa-xmark"></i> Close
                        </button>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

